import React from 'react';
import { ShieldCheck, Leaf, Phone, Mail, MapPin, Scissors, TreeDeciduous, XCircle, CloudLightning, PhoneCallIcon, DollarSign } from 'lucide-react';

// Define the custom colors based on the user's request
const primaryColor = '#D17F38'; // Orange/Brown
const secondaryColor = '#050A03'; // Dark Black/Charcoal

function App() {
  return (
    <div className="min-h-screen font-inter" style={{ backgroundColor: secondaryColor, color: '#F0F0F0' }}>
      {/* Header */}
      <header className="py-4 px-6 md:px-12 flex justify-between items-center shadow-lg" style={{ backgroundColor: secondaryColor }}>
        <div className="text-3xl font-bold" style={{ color: primaryColor }}>
          MTS
        </div>
        <nav>
          <ul className="flex space-x-6">
            <li><a href="#peace-of-mind" className="hover:underline text-lg">Why Choose MTS</a></li>
            <li><a href="#services" className="hover:underline text-lg">Services</a></li>
            <li><a href="#contact" className="hover:underline text-lg">Contact</a></li>
          </ul>
        </nav>
      </header>

      {/* Hero Section */}
      <section className="relative h-96 flex items-center justify-center text-center px-4" style={{ backgroundColor: secondaryColor }}>
        <div className="relative z-10 p-6 rounded-lg bg-opacity-70" style={{ backgroundColor: secondaryColor }}>
          <img
            src="/logo192.png" // Path to your logo in the public folder
            alt="MTS Logo"
            className="block mx-auto mb-4 h-48 w-auto rounded-full shadow-lg" // Centered, spaced, and sized for aesthetics
            onError={(e) => { e.target.onerror = null; e.target.src = 'https://placehold.co/96x96/D17F38/050A03?text=Logo'; }} // Fallback image
          />
          
          <h1 className="text-5xl md:text-7xl font-extrabold mb-4" style={{ color: primaryColor }}>
            McKibben Tree Services
          </h1>
          <p className="text-xl md:text-2xl max-w-3xl mx-auto">
            Your trusted partner for professional tree care in Northern Ireland.
          </p>
        </div>
      </section>

      {/* Why Choose MTS */}
      <section id="peace-of-mind" className="py-16 px-6 md:px-12 lg:px-24">
        <h2 className="text-4xl font-bold mb-10 text-center" style={{ color: primaryColor }}>
          <ShieldCheck className="inline-block mr-3 text-5xl" />Why Choose MTS
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <div className="bg-gray-800 p-8 rounded-lg shadow-xl flex flex-col items-center text-center">
            <ShieldCheck className="text-6xl mb-4" style={{ color: primaryColor }} />
            <h3 className="text-2xl font-semibold mb-3">Fully Insured & Prioritise Safety</h3>
            <p className="text-lg">Your peace of mind is important to us. MTS is fully insured and prioritises safety, ensuring every job is done with care.</p>
          </div>
          <div className="bg-gray-800 p-8 rounded-lg shadow-xl flex flex-col items-center text-center">
            <Leaf className="text-6xl mb-4" style={{ color: primaryColor }} />
            <h3 className="text-2xl font-semibold mb-3">Excellent Customer Service</h3>
            <p className="text-lg">From the initial consultation to project completion, MTS is committed to providing excellent customer service and ensuring you're completely satisfied with our work.</p>
          </div>
          <div className="bg-gray-800 p-8 rounded-lg shadow-xl flex flex-col items-center text-center">
            <DollarSign className="text-6xl mb-4" style={{ color: primaryColor }} />
            <h3 className="text-2xl font-semibold mb-3">Competitive Pricing & Professional Job</h3>
            <p className="text-lg">MTS offers competitive pricing with no hidden fees. You'll always know what to expect upfront. Following the highest safety and quality standards in all our work, you can be assured you are getting a professional job by someone who cares about trees.</p>
          </div>
        </div>
      </section>

      {/* Why We Are Different Section */}
      <section className="py-16 px-6 md:px-12 lg:px-24" style={{ backgroundColor: secondaryColor }}>
        <h2 className="text-4xl font-bold mb-10 text-center" style={{ color: primaryColor }}>
          Why We Are Different
        </h2>
        <div className="max-w-4xl mx-auto text-lg text-center">
          <p className="mb-4">
            MTS specialises in providing top-quality tree surgery services to residential, commercial and municipal clients in the County Down, Armagh and Antrim areas of Northern Ireland.
          </p>
          <p>
            As a fully qualified tree surgeon, I am dedicated to ensuring the health, safety and beauty of your trees.
          </p>
        </div>
      </section>

      {/* Our Services Section */}
      <section id="services" className="py-16 px-6 md:px-12 lg:px-24 bg-gray-900">
        <h2 className="text-4xl font-bold mb-10 text-center" style={{ color: primaryColor }}>
          Our Services
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {/* Service Card: Pruning & Trimming */}
          <div className="bg-gray-800 p-8 rounded-lg shadow-xl flex flex-col items-center text-center">
            <Scissors className="text-6xl mb-4" style={{ color: primaryColor }} />
            <h3 className="text-2xl font-semibold mb-3">Pruning & Trimming</h3>
            <p className="text-lg">Enhance the shape, health and safety of your trees with our expert pruning and trimming services. Whether it's reducing overgrowth or removing deadwood, we ensure your trees stay vibrant and safe.</p>
          </div>

          {/* Service Card: Tree Removal */}
          <div className="bg-gray-800 p-8 rounded-lg shadow-xl flex flex-col items-center text-center">
            <XCircle className="text-6xl mb-4" style={{ color: primaryColor }} />
            <h3 className="text-2xl font-semibold mb-3">Tree Removal</h3>
            <p className="text-lg">When a tree poses a risk or needs to be cleared for other purposes, we provide efficient, safe and controlled tree removal services. Tree Removal services are available. MTS loves trees and our main objective is to maintain and prolong trees, however, sometimes trees need to be removed for different reasons and we are here to provide a safe and expert service. Where a tree is close to a building or it is not possible to fell, we can use different methods to safely dismantle the tree.</p>
          </div>

          {/* Service Card: Stump Grinding */}
          <div className="bg-gray-800 p-8 rounded-lg shadow-xl flex flex-col items-center text-center">
            <TreeDeciduous className="text-6xl mb-4" style={{ color: primaryColor }} />
            <h3 className="text-2xl font-semibold mb-3">Stump Grinding</h3>
            <p className="text-lg">After a tree is removed, we can grind down the stump to prevent regrowth and make your property tidy and safe.</p>
          </div>

          {/* Service Card: Emergency Tree Services */}
          <div className="bg-gray-800 p-8 rounded-lg shadow-xl flex flex-col items-center text-center">
            <CloudLightning className="text-6xl mb-4" style={{ color: primaryColor }} />
            <h3 className="text-2xl font-semibold mb-3">Emergency Services</h3>
            <p className="text-lg">Storm damage, fallen trees or dangerous branches? We can provide emergency tree services when you need them most.</p>
          </div>

          {/* Service Card: Crown Lifting */}
          <div className="bg-gray-800 p-8 rounded-lg shadow-xl flex flex-col items-center text-center">
            <Leaf className="text-6xl mb-4" style={{ color: primaryColor }} />
            <h3 className="text-2xl font-semibold mb-3">Crown Lifting</h3>
            <p className="text-lg">Crown lifting is a tree care technique that involves the removal of the lower branches of a tree to increase the clearance between the ground and the tree’s lower branches. This process is typically carried out to improve light penetration for gardens, the tree’s appearance and to reduce the weight of the tree. Lifting the crown allows better access to areas under the tree, such as pathways, driveways, or gardens. It also helps to clear visibility, particularly along roadsides or pathways.</p>
          </div>

          {/* Service Card: Crown Reduction & Reshaping */}
          <div className="bg-gray-800 p-8 rounded-lg shadow-xl flex flex-col items-center text-center">
            <Scissors className="text-6xl mb-4" style={{ color: primaryColor }} />
            <h3 className="text-2xl font-semibold mb-3">Crown Reduction & Reshaping</h3>
            <p className="text-lg">Crown Reduction & Crown Reshaping can help to balance the height and width of your tree without having a negative impact on its health. It’s perfect for trees which have become overgrown and need a trim to restore their shape and improve their appearance.</p>
          </div>

          {/* Service Card: Formative Pruning */}
          <div className="bg-gray-800 p-8 rounded-lg shadow-xl flex flex-col items-center text-center">
            <TreeDeciduous className="text-6xl mb-4" style={{ color: primaryColor }} />
            <h3 className="text-2xl font-semibold mb-3">Formative Pruning</h3>
            <p className="text-lg">Formative pruning is the practice of shaping trees during their early growth stages and as they mature to encourage a strong, healthy structure. This type of pruning helps guide the tree’s development, ensuring it grows with well-spaced, strong branches and an even canopy. By starting formative pruning early, we can prevent future issues, such as weak growth patterns or potential structural problems which can be costly down the line.</p>
          </div>

          {/* Service Card: Deadwood Removal */}
          <div className="bg-gray-800 p-8 rounded-lg shadow-xl flex flex-col items-center text-center">
            <XCircle className="text-6xl mb-4" style={{ color: primaryColor }} />
            <h3 className="text-2xl font-semibold mb-3">Deadwood Removal</h3>
            <p className="text-lg">Deadwood Removal in trees is an eyesore, it can also be dangerous as deadwood can fall from the tree at anytime, no matter the weather of wind speed. MTS can expertly and safely remove deadwood from trees before they cause injury and/or property damage.</p>
          </div>

          {/* Service Card: Crown Thinning */}
          <div className="bg-gray-800 p-8 rounded-lg shadow-xl flex flex-col items-center text-center">
            <Leaf className="text-6xl mb-4" style={{ color: primaryColor }} />
            <h3 className="text-2xl font-semibold mb-3">Crown Thinning</h3>
            <p className="text-lg">Crown Thinning is the perfect choice if a tree’s crown is cluttered and dense, it can have a negative effect on the flow of air and light through the tree. Crown thinning reduces the crown’s density, ultimately helping your trees to live longer, healthier lives and can significantly improve the appearance of the tree.</p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-16 px-6 md:px-12 lg:px-24 text-center" style={{ backgroundColor: secondaryColor }}>
        <h2 className="text-4xl font-bold mb-10" style={{ color: primaryColor }}>
          Contact Us
        </h2>
        <div className="max-w-2xl mx-auto bg-gray-800 p-8 rounded-lg shadow-xl">
          <p className="text-xl mb-4">
            Based in Dromore, Co. Down, Northern Ireland.
          </p>
          <div className="flex flex-col items-center space-y-4">
            <a href="mailto:Peter@mckibbentreeservices.co.uk" className="flex items-center text-xl hover:underline" style={{ color: primaryColor }}>
              <Mail className="mr-3" /> Peter@mckibbentreeservices.co.uk
            </a>
            <a href="tel:07827299153" className="flex items-center text-xl hover:underline" style={{ color: primaryColor }}>
              <Phone className="mr-3" /> 07827299153
            </a>
            <div className="flex items-center text-xl">
              <MapPin className="mr-3" style={{ color: primaryColor }} /> Dromore, Co. Down
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 md:px-12 text-center text-gray-400" style={{ backgroundColor: secondaryColor }}>
        <p>&copy; {new Date().getFullYear()} MTS. All rights reserved.</p>
      </footer>
    </div>
  );
}

export default App;